/** BFP-1143 — survey-driven responder profile attributes and audience filters. */

import { SENATE_DISTRICT_OPTIONS } from './audience-publish'

export const FRESHNESS_TAGS = ['PERM', 'R1M', 'R3M', 'R6M', 'R12M'] as const
export type FreshnessTag = (typeof FRESHNESS_TAGS)[number]

export const PROFILE_SYSTEM_KEYS = [
  'currentVoterRegistration',
  'registeredToVote',
  'gender',
  'senateDistrict',
  'zipCode',
  'raceEthnicity',
  'relationshipStatus',
  'marriageHistory',
  'children',
  'householdSize',
  'educationLevel',
  'employmentStatus',
  'housingStatus',
] as const
export type ProfileSystemKey = (typeof PROFILE_SYSTEM_KEYS)[number]

export const PREFER_NOT_TO_ANSWER = 'Prefer not to answer' as const

export const PROFILE_ZIP_CODE_OPTIONS = [] as const

export const PROFILE_RACE_ETHNICITY_OPTIONS = [
  'White',
  'Black or African American',
  'American Indian or Alaska Native',
  'Hispanic or Latino',
  'Native Hawaiian or Pacific Islander',
  'Asian',
  'Middle Eastern or North African',
  PREFER_NOT_TO_ANSWER,
] as const

export const PROFILE_RELATIONSHIP_STATUS_OPTIONS = [
  'Married and living with my spouse',
  'Living with a partner',
  'Single / not currently in a relationship',
  'Married but currently living apart',
  'In a relationship but not living together',
  'Separated',
  'Divorced',
  'Widowed',
  PREFER_NOT_TO_ANSWER,
] as const

export const PROFILE_MARRIAGE_HISTORY_OPTIONS = [
  'I have never been married',
  'Once',
  'Twice',
  'Three times',
  'Four or more times',
  PREFER_NOT_TO_ANSWER,
] as const

export const PROFILE_CHILDREN_OPTIONS = [
  'Child/children under 6 living at home',
  'Age 6–12 living at home',
  'Age 13–17 living at home',
  'Under 18 living somewhere else',
  'Adult child/children living at home',
  'Adult child/children living somewhere else',
  'I do not have children',
  PREFER_NOT_TO_ANSWER,
] as const

export const PROFILE_HOUSEHOLD_SIZE_OPTIONS = ['1', '2', '3', '4', '5', '6', '7+'] as const

export const PROFILE_EDUCATION_LEVEL_OPTIONS = [
  'Less than high school',
  'High school diploma or GED',
  'Trade/technical/vocational training',
  'Some college, no degree',
  'Associate degree',
  "Bachelor's degree",
  "Master's degree",
  'Professional degree such as JD/MD/DDS/PharmD',
  'Doctoral degree such as PhD/EdD',
  PREFER_NOT_TO_ANSWER,
] as const

export const PROFILE_EMPLOYMENT_STATUS_OPTIONS = [
  'Working full-time for an employer',
  'Working part-time for an employer',
  'Self-employed, freelance, or an independent contractor',
  'I own or run a business with employees',
  'Student',
  'Homemaker or full-time caregiver',
  'Unemployed and looking for work',
  'Not currently working and not looking for work',
  'Retired',
  'Unable to work',
  PREFER_NOT_TO_ANSWER,
] as const

export const PROFILE_HOUSING_STATUS_OPTIONS = [
  'Own with mortgage/loan',
  'Own outright',
  'Rent',
  'Live with family/friends and do not pay rent',
  'Provided through employer/military/school/another organization',
  PREFER_NOT_TO_ANSWER,
] as const

export const SYSTEM_PROFILE_QUESTION_TYPES = ['multiple_choice', 'checkbox', 'short_text'] as const
export type SystemProfileQuestionType = (typeof SYSTEM_PROFILE_QUESTION_TYPES)[number]

export type SystemProfileAttributeSeed = {
  systemKey: ProfileSystemKey
  name: string
  prompt: string
  options: readonly string[]
  questionType: SystemProfileQuestionType
}

/** Question types that can update a responder profile. */
export const PROFILE_UPDATE_QUESTION_TYPES = [
  'multiple_choice',
  'yes_no',
  'checkbox',
  'dropdown',
  'short_text',
] as const
export type ProfileUpdateQuestionType = (typeof PROFILE_UPDATE_QUESTION_TYPES)[number]

export const CURRENT_VOTER_REGISTRATION_OPTIONS = [
  'Independent',
  'Democrat',
  'Republican',
  'Other',
] as const

export const REGISTERED_TO_VOTE_OPTIONS = ['Yes', 'No', 'Not Sure'] as const

export const PROFILE_GENDER_OPTIONS = ['Male', 'Female', 'Other'] as const

/** Display label → value stored on `user_profiles.gender`. */
export const PROFILE_GENDER_TO_STORED: Record<(typeof PROFILE_GENDER_OPTIONS)[number], string> = {
  Male: 'male',
  Female: 'female',
  Other: 'non_binary',
}

export function isFreshnessTag(value: unknown): value is FreshnessTag {
  return typeof value === 'string' && (FRESHNESS_TAGS as readonly string[]).includes(value)
}

export function isProfileSystemKey(value: unknown): value is ProfileSystemKey {
  return typeof value === 'string' && (PROFILE_SYSTEM_KEYS as readonly string[]).includes(value)
}

export function isProfileUpdateQuestionType(value: unknown): value is ProfileUpdateQuestionType {
  return (
    typeof value === 'string' &&
    (PROFILE_UPDATE_QUESTION_TYPES as readonly string[]).includes(value)
  )
}

/** One selected audience filter. `systemKey` is copied from the attribute for match and future assignment. */
export interface ProfileAttributeFilter {
  attributeId: string
  values: string[]
  systemKey?: ProfileSystemKey | null
}

/**
 * Resolved by campaign before the platform match query.
 * `name` is the stored profile field (camelCase or systemKey), not the filter label.
 * Column-backed gender, senate, and voter registration use `systemKey` and set `name` to that column.
 */
export interface ResolvedProfileAttributeFilter {
  attributeId: string
  systemKey: ProfileSystemKey | null
  name: string
  values: string[]
}

/** Signup columns and identity fields a survey answer must not replace. */
export const RESERVED_PROFILE_STORAGE_KEYS = [
  '_id',
  'id',
  'userId',
  'email',
  'firstName',
  'lastName',
  'status',
  'createdAt',
  'updatedAt',
  'password',
  'profileAttributes',
  'gender',
  'education',
  'employment',
  'maritalStatus',
  'householdSize',
  'numberOfChildren',
  'industry',
  'housingStatus',
  'senateDistrict',
  'registeredPoliticalParty',
  'registeredToVote',
  'currentVoterRegistration',
  'consent',
  'isVerified',
  'instrumentLinkVerifiedAt',
  'instrumentVerificationFailed',
  'signalProviderId',
  'requiresPasswordReset',
  'locationData',
  'dateOfBirth',
  'phone',
  'displayName',
  'phoneVerified',
  'phoneVerifiedAt',
  'avatarUrl',
  'ekyc',
  'webSurveySignup',
  'sourceAudience',
  'deviceTypes',
  'surveySourcedFieldIds',
  'surveyDemographicsIngest',
] as const

const RESERVED_PROFILE_STORAGE_KEY_SET = new Set<string>(RESERVED_PROFILE_STORAGE_KEYS)

/** Answers for these columns are copied onto the column. They do not get a second field. */
const MIRRORED_PROFILE_COLUMNS = new Set([
  'gender',
  'senateDistrict',
  'currentVoterRegistration',
  'registeredToVote',
])

/** `Paid Salary` → `paidSalary`. Punctuation and spaces are dropped. */
export function camelCaseProfileAttributeKey(name: string): string {
  const parts = name.trim().match(/[A-Za-z0-9]+/g)
  if (!parts || parts.length === 0) return ''
  const [first, ...rest] = parts
  return (
    first.toLowerCase() +
    rest.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join('')
  )
}

export function isSafeProfileAttributeStorageKey(key: string): boolean {
  return (
    key.length > 0 &&
    !key.startsWith('$') &&
    !key.includes('.') &&
    !RESERVED_PROFILE_STORAGE_KEY_SET.has(key)
  )
}

function profilePrefixedKey(key: string): string {
  if (!key) return ''
  return `profile${key.charAt(0).toUpperCase()}${key.slice(1)}`
}

/**
 * Field name written on `user_profiles`.
 * Uses `systemKey` when that key is free (`marriageHistory`, `employmentStatus`).
 * Otherwise camelCase of the display name (`paidSalary`).
 * A key that would overwrite a signup column is stored beside it (`profileHousingStatus`).
 * Gender, senate district, and voter registration return null — those columns are updated directly.
 */
export function profileAttributeStorageKey(name: string, systemKey?: string | null): string | null {
  const system = typeof systemKey === 'string' ? systemKey.trim() : ''
  if (system) {
    if (isSafeProfileAttributeStorageKey(system)) return system
    if (MIRRORED_PROFILE_COLUMNS.has(system)) return null
    const prefixed = profilePrefixedKey(system)
    return isSafeProfileAttributeStorageKey(prefixed) ? prefixed : null
  }
  const camel = camelCaseProfileAttributeKey(name)
  if (isSafeProfileAttributeStorageKey(camel)) return camel
  const prefixed = profilePrefixedKey(camel)
  return isSafeProfileAttributeStorageKey(prefixed) ? prefixed : null
}

export interface IProfileAttribute {
  id: string
  name: string
  freshnessTag: FreshnessTag
  options: string[]
  systemKey: ProfileSystemKey | null
  questionBankEntryId: string | null
}

export function isFreeTextProfileAttribute(
  attribute: Pick<IProfileAttribute, 'systemKey' | 'options'>,
): boolean {
  return attribute.systemKey === 'zipCode' || attribute.options.length === 0
}

export interface IProfileAttributeListResponse {
  attributes: IProfileAttribute[]
}

export interface IProfileAttributeSimilarMatch {
  questionBankEntryId: string
  profileAttributeId: string
  prompt: string
  attributeName: string
  options: string[]
  freshnessTag: FreshnessTag
  type: string
  systemKey: ProfileSystemKey | null
  score: number
}

export interface IProfileAttributeSimilarResponse {
  match: IProfileAttributeSimilarMatch | null
}

/** Current value stored on `user_profiles.profileAttributes`. */
export interface IProfileAttributeCurrentValue {
  attributeId: string
  systemKey: ProfileSystemKey | null
  rawAnswer: string
  normalizedValues: string[]
  observedAt: string
  freshnessTag: FreshnessTag
  refreshEligibleAt: string | null
  sourceInstrumentId: string
  questionId: string
  questionVersion: string | null
}

/** Identity S2S body — response service sends this after a profile-update answer. */
export interface IRecordProfileAttributeAnswerBody {
  responseId: string
  questionId: string
  attributeId: string
  systemKey?: ProfileSystemKey | null
  rawAnswer: string
  normalizedValues: string[]
  freshnessTag: FreshnessTag
  observedAt: string
  refreshEligibleAt: string | null
  sourceInstrumentId: string
  questionVersion?: string | null
}

/** Idempotent catalog so Target Audience can filter before a researcher authors a question. */
export const SYSTEM_PROFILE_ATTRIBUTE_SEEDS: readonly SystemProfileAttributeSeed[] = [
  {
    systemKey: 'currentVoterRegistration',
    name: 'Current Voter Registration',
    prompt: 'What is your current voter registration?',
    options: CURRENT_VOTER_REGISTRATION_OPTIONS,
    questionType: 'multiple_choice',
  },
  {
    systemKey: 'registeredToVote',
    name: 'Currently Registered to Vote',
    prompt: 'Are you currently registered to vote?',
    options: REGISTERED_TO_VOTE_OPTIONS,
    questionType: 'multiple_choice',
  },
  {
    systemKey: 'gender',
    name: 'Gender',
    prompt: 'What is your gender?',
    options: PROFILE_GENDER_OPTIONS,
    questionType: 'multiple_choice',
  },
  {
    systemKey: 'senateDistrict',
    name: 'Senate District',
    prompt: 'What is your senate district?',
    options: SENATE_DISTRICT_OPTIONS,
    questionType: 'multiple_choice',
  },
  {
    systemKey: 'zipCode',
    name: 'ZIP Code',
    prompt: 'What is your ZIP code?',
    options: PROFILE_ZIP_CODE_OPTIONS,
    questionType: 'short_text',
  },
  {
    systemKey: 'raceEthnicity',
    name: 'Race / Ethnicity',
    prompt: 'What is your race / ethnicity?',
    options: PROFILE_RACE_ETHNICITY_OPTIONS,
    questionType: 'checkbox',
  },
  {
    systemKey: 'relationshipStatus',
    name: 'Relationship Status',
    prompt: 'What is your relationship status?',
    options: PROFILE_RELATIONSHIP_STATUS_OPTIONS,
    questionType: 'multiple_choice',
  },
  {
    systemKey: 'marriageHistory',
    name: 'Marriage History / Number of Times Married',
    prompt: 'What is your marriage history / number of times married?',
    options: PROFILE_MARRIAGE_HISTORY_OPTIONS,
    questionType: 'multiple_choice',
  },
  {
    systemKey: 'children',
    name: 'Children',
    prompt: 'Do you have children?',
    options: PROFILE_CHILDREN_OPTIONS,
    questionType: 'checkbox',
  },
  {
    systemKey: 'householdSize',
    name: 'Household Size',
    prompt: 'What is your household size?',
    options: PROFILE_HOUSEHOLD_SIZE_OPTIONS,
    questionType: 'multiple_choice',
  },
  {
    systemKey: 'educationLevel',
    name: 'Education Level',
    prompt: 'What is your education level?',
    options: PROFILE_EDUCATION_LEVEL_OPTIONS,
    questionType: 'multiple_choice',
  },
  {
    systemKey: 'employmentStatus',
    name: 'Employment Status',
    prompt: 'What is your employment status?',
    options: PROFILE_EMPLOYMENT_STATUS_OPTIONS,
    questionType: 'checkbox',
  },
  {
    systemKey: 'housingStatus',
    name: 'Housing Status',
    prompt: 'What is your housing status?',
    options: PROFILE_HOUSING_STATUS_OPTIONS,
    questionType: 'multiple_choice',
  },
]

/** Used when tenant rows are missing so Target Audience can still filter. */
export function systemProfileAttributesAsPublic(): IProfileAttribute[] {
  return SYSTEM_PROFILE_ATTRIBUTE_SEEDS.map((seed) => ({
    id: seed.systemKey,
    name: seed.name,
    freshnessTag: 'PERM',
    options: [...seed.options],
    systemKey: seed.systemKey,
    questionBankEntryId: null,
  }))
}
